/**
 * NDT write dialogs: technician cert, welder, welder qualification, add
 * instrument, edit specs, record calibration, source, custody event, dose, and
 * a delete confirm.
 *
 * VALUES GO TO THE API IN THE TENANT'S UNITS, AS TYPED. Inputs are labelled
 * with `meta.units` and nothing here converts; the API converts mm / GBq / mSv
 * itself. A dialog whose unit has not arrived does not open.
 *
 * WRITES ARE snake_case. Every rule the API validates is the API's: a 400
 * arrives with its own message and is shown as sent. The dialogs check only
 * what would otherwise go out as a type the API cannot read.
 *
 * DATES are `<input type="date">` strings (YYYY-MM-DD) sent as typed; a default
 * comes from `meta.today` (the tenant's day), never the browser's clock.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  NdtSpecsAttachError,
  ndtPersonName,
  useAddNdtInstrument,
  useAddNdtQual,
  useCreateNdtCert,
  useCreateNdtDose,
  useCreateNdtSource,
  useCreateNdtWelder,
  useLogNdtSource,
  useNdtCatalog,
  useNdtEquipmentOptions,
  useNdtPeople,
  useRecordNdtCalibration,
  useUpdateNdtCert,
  useUpdateNdtDose,
  useUpdateNdtQual,
  useUpdateNdtSource,
  useUpdateNdtSpecs,
  useUpdateNdtWelder,
} from '../../hooks/useNdtInspection';
import {
  inputValue,
  NDT_DOSIMETER_KIND_LABEL,
  NDT_INSTRUMENT_KIND_LABEL,
  NDT_KIND_EQUIPMENT_TYPE,
  NDT_SOURCE_STATUS_SKIN,
  ndtErrorMessage,
  ndtSourceLogErrorMessage,
  ndtSourceStatusSkin,
  ndtSourceWriteErrorMessage,
  numberFromInput,
} from '../../lib/ndt-inspection';
import {
  NDT_DOSIMETER_KINDS,
  NDT_INSTRUMENT_KINDS,
  NDT_SOURCE_SETTABLE_STATUSES,
  type NdtCertWrite,
  type NdtDose,
  type NdtDoseWrite,
  type NdtInstrument,
  type NdtInstrumentKind,
  type NdtQualWrite,
  type NdtSource,
  type NdtSourceEvent,
  type NdtSourceWrite,
  type NdtTechnicianCert,
  type NdtUnits,
  type NdtWelder,
  type NdtWelderQual,
} from '../../types/ndt-inspection';
import {
  NDT_DATE_INPUT,
  NDT_INPUT,
  NDT_PRIMARY,
  NDT_SECONDARY,
  NdtDialog,
  NdtField,
  NdtFormError,
} from './NdtBits';

export type Change = React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;

/** Blank → null, else trimmed. */
export const textOrNull = (v: string) => (v.trim() ? v.trim() : null);

export function Footer({ formId, pending, label, onClose }: { formId: string; pending: boolean; label: string; onClose: () => void }) {
  return (
    <>
      <button type="button" className={NDT_SECONDARY} onClick={onClose}>
        Cancel
      </button>
      <button type="submit" form={formId} className={NDT_PRIMARY} disabled={pending}>
        {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
        {label}
      </button>
    </>
  );
}

export function useForm<T>(open: boolean, initial: () => T) {
  const [v, setV] = useState<T>(initial);
  const [start, setStart] = useState<T>(initial);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      const first = initial();
      setV(first);
      setStart(first);
      setError(null);
    }
    // Reset only when the dialog opens; a refetch must not wipe a half-typed form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k: keyof T) => (e: Change) => setV((prev) => ({ ...prev, [k]: e.target.value }));
  return { v, setV, set, error, setError, start };
}

/**
 * On an edit, send only what the reader changed. A figure the API returned in
 * the tenant's unit is rounded for display; echoing it back unchanged would be
 * converted to canonical units again and drift by the rounding. Keys of `body`
 * match the form's keys.
 */
export function changedOnly<B extends object>(body: B, v: object, start: object): B {
  const out: Record<string, unknown> = {};
  for (const [k, value] of Object.entries(body)) {
    if ((v as Record<string, unknown>)[k] !== (start as Record<string, unknown>)[k]) out[k] = value;
  }
  return out as B;
}

export function PeopleSelect({ value, onChange, required, label }: { value: string; onChange: (e: Change) => void; required?: boolean; label: string }) {
  const people = useNdtPeople();
  return (
    <NdtField label={label} hint={people.isError ? 'People could not be loaded.' : undefined}>
      <select className={NDT_INPUT} value={value} onChange={onChange} required={required}>
        <option value="">{people.isLoading ? 'Loading…' : required ? 'Choose a person' : 'Nobody'}</option>
        {(people.data ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {ndtPersonName(u)}
          </option>
        ))}
      </select>
    </NdtField>
  );
}

// ─── Technician cert ─────────────────────────────────────────────────────────

interface CertValues {
  user_id: string;
  method: string;
  level: string;
  scheme: string;
  sector: string;
  certificate_number: string;
  issuing_body: string;
  issued_on: string;
  expires_on: string;
  notes: string;
}

/** Create (no `cert`) or edit a technician cert. A levelless method sends `level: null`. */
export function CertDialog({ open, onClose, cert, defaultUserId }: { open: boolean; onClose: () => void; cert?: NdtTechnicianCert | null; defaultUserId?: string }) {
  const catalog = useNdtCatalog();
  const create = useCreateNdtCert();
  const update = useUpdateNdtCert();
  const { v, set, error, setError } = useForm<CertValues>(open, () => ({
    user_id: cert?.userId ?? defaultUserId ?? '',
    method: cert?.method ?? '',
    level: cert?.level != null ? String(cert.level) : '',
    scheme: cert?.scheme ?? '',
    sector: cert?.sector ?? '',
    certificate_number: cert?.certificateNumber ?? '',
    issuing_body: cert?.issuingBody ?? '',
    issued_on: cert?.issuedOn ?? '',
    expires_on: cert?.expiresOn ?? '',
    notes: cert?.notes ?? '',
  }));
  const cat = catalog.data?.data;
  const levelless = !!cat && cat.levellessMethods.includes(v.method);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.user_id) return setError('Choose the technician.');
    if (!v.method) return setError('Choose the method.');
    if (!v.scheme) return setError('Choose the scheme.');
    if (!levelless && !v.level) return setError('Choose the level.');
    const body: NdtCertWrite = {
      user_id: v.user_id,
      method: v.method,
      level: levelless ? null : Number(v.level),
      scheme: v.scheme,
      sector: textOrNull(v.sector),
      certificate_number: textOrNull(v.certificate_number),
      issuing_body: textOrNull(v.issuing_body),
      issued_on: v.issued_on || null,
      expires_on: v.expires_on || null,
      notes: textOrNull(v.notes),
    };
    const done = {
      onSuccess: () => {
        toast.success(cert ? 'Certification saved' : 'Certification added');
        onClose();
      },
      onError: (err: unknown) => setError(ndtErrorMessage(err, 'The certification could not be saved.')),
    };
    if (cert) update.mutate({ id: cert.id, body }, done);
    else create.mutate(body, done);
  };

  return (
    <NdtDialog
      open={open}
      title={cert ? 'Edit certification' : 'Add certification'}
      onClose={onClose}
      wide
      footer={<Footer formId="ndt-cert" pending={create.isPending || update.isPending} label="Save" onClose={onClose} />}
    >
      <form id="ndt-cert" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PeopleSelect label="Technician" value={v.user_id} onChange={set('user_id')} required />
          <NdtField label="Method">
            <select className={NDT_INPUT} value={v.method} onChange={set('method')}>
              <option value="">Choose a method</option>
              {(cat?.methods ?? []).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Level" hint={levelless ? 'This credential has no level.' : undefined}>
            <select className={NDT_INPUT} value={levelless ? '' : v.level} onChange={set('level')} disabled={levelless}>
              <option value="">{levelless ? '—' : 'Choose a level'}</option>
              <option value="1">Level I</option>
              <option value="2">Level II</option>
              <option value="3">Level III</option>
            </select>
          </NdtField>
          <NdtField label="Scheme">
            <select className={NDT_INPUT} value={v.scheme} onChange={set('scheme')}>
              <option value="">Choose a scheme</option>
              {(cat?.schemes ?? []).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Sector">
            <input className={NDT_INPUT} value={v.sector} onChange={set('sector')} placeholder="e.g. Welds" />
          </NdtField>
          <NdtField label="Certificate #">
            <input className={NDT_INPUT} value={v.certificate_number} onChange={set('certificate_number')} />
          </NdtField>
          <NdtField label="Issuing body">
            <input className={NDT_INPUT} value={v.issuing_body} onChange={set('issuing_body')} />
          </NdtField>
          <div />
          <NdtField label="Issued">
            <input type="date" className={NDT_DATE_INPUT} value={v.issued_on} onChange={set('issued_on')} />
          </NdtField>
          <NdtField label="Expires" hint="Leave blank for a credential with no expiry.">
            <input type="date" className={NDT_DATE_INPUT} value={v.expires_on} onChange={set('expires_on')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
        {catalog.isError && <p className="text-xs text-fieldiq-muted">The method and scheme lists could not be loaded.</p>}
      </form>
    </NdtDialog>
  );
}

// ─── Welder ──────────────────────────────────────────────────────────────────

interface WelderValues {
  name: string;
  welder_stamp: string;
  employer: string;
  user_id: string;
  notes: string;
}

export function WelderDialog({ open, onClose, welder, onCreated }: { open: boolean; onClose: () => void; welder?: NdtWelder | null; onCreated?: (id: string) => void }) {
  const create = useCreateNdtWelder();
  const update = useUpdateNdtWelder();
  const { v, set, error, setError } = useForm<WelderValues>(open, () => ({
    name: welder?.name ?? '',
    welder_stamp: welder?.welderStamp ?? '',
    employer: welder?.employer ?? '',
    user_id: welder?.userId ?? '',
    notes: welder?.notes ?? '',
  }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.name.trim()) return setError('Name is required.');
    const body = {
      name: v.name.trim(),
      welder_stamp: textOrNull(v.welder_stamp),
      employer: textOrNull(v.employer),
      user_id: v.user_id || null,
      notes: textOrNull(v.notes),
    };
    const onError = (err: unknown) => setError(ndtErrorMessage(err, 'The welder could not be saved.'));
    if (welder) {
      update.mutate({ id: welder.id, body }, { onSuccess: () => { toast.success('Welder saved'); onClose(); }, onError });
    } else {
      create.mutate(body, {
        onSuccess: (res) => {
          toast.success('Welder added');
          onClose();
          if (res.data?.id) onCreated?.(res.data.id);
        },
        onError,
      });
    }
  };

  return (
    <NdtDialog
      open={open}
      title={welder ? 'Edit welder' : 'Add welder'}
      onClose={onClose}
      footer={<Footer formId="ndt-welder" pending={create.isPending || update.isPending} label="Save" onClose={onClose} />}
    >
      <form id="ndt-welder" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <NdtField label="Name">
            <input className={NDT_INPUT} value={v.name} onChange={set('name')} required />
          </NdtField>
          <NdtField label="Welder stamp">
            <input className={NDT_INPUT} value={v.welder_stamp} onChange={set('welder_stamp')} />
          </NdtField>
          <NdtField label="Employer" hint="The customer or contractor they weld for.">
            <input className={NDT_INPUT} value={v.employer} onChange={set('employer')} />
          </NdtField>
          <PeopleSelect label="FieldIQ user (optional)" value={v.user_id} onChange={set('user_id')} />
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

// ─── Welder qualification ────────────────────────────────────────────────────

interface QualValues {
  code: string;
  process: string;
  position: string;
  progression: string;
  filler_class: string;
  diameter_min: string;
  diameter_max: string;
  wall_min: string;
  wall_max: string;
  qualified_on: string;
  expires_on: string;
  continuity_on: string;
  test_report_ref: string;
  notes: string;
}

export function QualDialog({
  open,
  onClose,
  welderId,
  qual,
  units,
}: {
  open: boolean;
  onClose: () => void;
  welderId: string;
  qual?: NdtWelderQual | null;
  units: NdtUnits | null;
}) {
  const catalog = useNdtCatalog();
  const add = useAddNdtQual();
  const update = useUpdateNdtQual();
  const { v, set, error, setError, start } = useForm<QualValues>(open, () => ({
    code: qual?.code ?? '',
    process: qual?.process ?? '',
    position: qual?.position ?? '',
    progression: qual?.progression ?? '',
    filler_class: qual?.fillerClass ?? '',
    diameter_min: inputValue(qual?.diameterMin),
    diameter_max: inputValue(qual?.diameterMax),
    wall_min: inputValue(qual?.wallMin),
    wall_max: inputValue(qual?.wallMax),
    qualified_on: qual?.qualifiedOn ?? '',
    expires_on: qual?.expiresOn ?? '',
    continuity_on: qual?.continuityOn ?? '',
    test_report_ref: qual?.testReportRef ?? '',
    notes: qual?.notes ?? '',
  }));
  const cat = catalog.data?.data;
  const len = units?.length ?? null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.code) return setError('Choose the code.');
    if (!v.process) return setError('Choose the process.');
    if (!v.qualified_on) return setError('Qualified date is required.');
    const nums = {
      diameter_min: numberFromInput(v.diameter_min),
      diameter_max: numberFromInput(v.diameter_max),
      wall_min: numberFromInput(v.wall_min),
      wall_max: numberFromInput(v.wall_max),
    };
    if (Object.values(nums).some((n) => Number.isNaN(n))) return setError('Diameter and wall must be numbers.');
    const body: NdtQualWrite = {
      code: v.code,
      process: v.process,
      position: v.position || null,
      progression: v.progression || null,
      filler_class: textOrNull(v.filler_class),
      ...nums,
      qualified_on: v.qualified_on,
      expires_on: v.expires_on || null,
      continuity_on: v.continuity_on || null,
      test_report_ref: textOrNull(v.test_report_ref),
      notes: textOrNull(v.notes),
    };
    const done = {
      onSuccess: () => {
        toast.success(qual ? 'Qualification saved' : 'Qualification added');
        onClose();
      },
      onError: (err: unknown) => setError(ndtErrorMessage(err, 'The qualification could not be saved.')),
    };
    if (qual) update.mutate({ id: qual.id, body: changedOnly(body, v, start) }, done);
    else add.mutate({ welderId, body }, done);
  };

  return (
    <NdtDialog
      open={open}
      title={qual ? 'Edit qualification' : 'Add qualification'}
      onClose={onClose}
      wide
      footer={<Footer formId="ndt-qual" pending={add.isPending || update.isPending} label="Save" onClose={onClose} />}
    >
      <form id="ndt-qual" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <NdtField label="Code">
            <select className={NDT_INPUT} value={v.code} onChange={set('code')}>
              <option value="">Choose a code</option>
              {(cat?.codes ?? []).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Process">
            <select className={NDT_INPUT} value={v.process} onChange={set('process')}>
              <option value="">Choose a process</option>
              {(cat?.weldProcesses ?? []).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Position">
            <select className={NDT_INPUT} value={v.position} onChange={set('position')}>
              <option value="">—</option>
              {(cat?.weldPositions ?? []).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Progression">
            <select className={NDT_INPUT} value={v.progression} onChange={set('progression')}>
              <option value="">—</option>
              {(cat?.progressions ?? []).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Filler class">
            <input className={NDT_INPUT} value={v.filler_class} onChange={set('filler_class')} placeholder="e.g. E6010" />
          </NdtField>
          <NdtField label="Test report #">
            <input className={NDT_INPUT} value={v.test_report_ref} onChange={set('test_report_ref')} />
          </NdtField>
          <NdtField label={`Diameter min${len ? ` (${len})` : ''}`}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.diameter_min} onChange={set('diameter_min')} />
          </NdtField>
          <NdtField label={`Diameter max${len ? ` (${len})` : ''}`}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.diameter_max} onChange={set('diameter_max')} />
          </NdtField>
          <NdtField label={`Wall min${len ? ` (${len})` : ''}`}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.wall_min} onChange={set('wall_min')} />
          </NdtField>
          <NdtField label={`Wall max${len ? ` (${len})` : ''}`}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.wall_max} onChange={set('wall_max')} />
          </NdtField>
          <NdtField label="Qualified">
            <input type="date" className={NDT_DATE_INPUT} value={v.qualified_on} onChange={set('qualified_on')} />
          </NdtField>
          <NdtField label="Expires" hint="Blank if the code sets no expiry.">
            <input type="date" className={NDT_DATE_INPUT} value={v.expires_on} onChange={set('expires_on')} />
          </NdtField>
          <NdtField label="Last used (continuity)" hint="Continuity lapses six months after this.">
            <input type="date" className={NDT_DATE_INPUT} value={v.continuity_on} onChange={set('continuity_on')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

// ─── Instruments ─────────────────────────────────────────────────────────────

interface InstrumentValues {
  mode: 'new' | 'existing';
  equipment_id: string;
  name: string;
  serial: string;
  manufacturer: string;
  model: string;
  kind: NdtInstrumentKind;
  interval: string;
  notes: string;
}

/** A whole number, null when blank, NaN when not a whole number. */
export function intFromInput(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : NaN;
}

/**
 * Add instrument — attach NDT specs to an EXISTING equipment unit, or create
 * the unit first (POST /equipment) and attach to it. When the attach fails
 * after a create, the new unit's id is kept and Save retries only the attach.
 */
export function AddInstrumentDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded?: (id: string) => void }) {
  const add = useAddNdtInstrument();
  const equipment = useNdtEquipmentOptions(open);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const { v, setV, set, error, setError } = useForm<InstrumentValues>(open, () => ({
    mode: 'new',
    equipment_id: '',
    name: '',
    serial: '',
    manufacturer: '',
    model: '',
    kind: 'exposure_device',
    interval: '',
    notes: '',
  }));
  useEffect(() => {
    if (open) setCreatedId(null);
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const interval = intFromInput(v.interval);
    if (Number.isNaN(interval)) return setError('Calibration interval must be a whole number of days.');
    if (v.mode === 'existing' && !createdId && !v.equipment_id) return setError('Choose the unit.');
    if (v.mode === 'new' && !createdId && !v.name.trim()) return setError('Name is required.');
    add.mutate(
      {
        equipmentId: createdId ?? (v.mode === 'existing' ? v.equipment_id : null),
        equipment:
          v.mode === 'new'
            ? {
                name: v.name.trim(),
                type: NDT_KIND_EQUIPMENT_TYPE[v.kind],
                serialNumber: textOrNull(v.serial),
                manufacturer: textOrNull(v.manufacturer),
                model: textOrNull(v.model),
              }
            : undefined,
        specs: { instrument_kind: v.kind, calibration_interval_days: interval, notes: textOrNull(v.notes) },
      },
      {
        onSuccess: (res) => {
          toast.success('Instrument added');
          onClose();
          if (res.data?.id) onAdded?.(res.data.id);
        },
        onError: (err) => {
          if (err instanceof NdtSpecsAttachError) {
            setCreatedId(err.equipmentId);
            setError(
              `${ndtErrorMessage(err.attachError, 'The NDT specs were not saved.')} The equipment unit was created; ` +
                'correct the specs and Save again to attach them to it.',
            );
          } else {
            setError(ndtErrorMessage(err, 'The instrument could not be added.'));
          }
        },
      },
    );
  };

  const locked = !!createdId;

  return (
    <NdtDialog
      open={open}
      title="Add instrument"
      onClose={onClose}
      wide
      footer={<Footer formId="ndt-add-instrument" pending={add.isPending} label={locked ? 'Save specs' : 'Add instrument'} onClose={onClose} />}
    >
      <form id="ndt-add-instrument" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="flex gap-4 text-sm text-fieldiq-text" role="radiogroup" aria-label="Which unit">
          <label className="inline-flex items-center gap-2">
            <input type="radio" name="ndt-unit-mode" checked={v.mode === 'new'} disabled={locked} onChange={() => setV((p) => ({ ...p, mode: 'new' }))} />
            New unit
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="radio" name="ndt-unit-mode" checked={v.mode === 'existing'} disabled={locked} onChange={() => setV((p) => ({ ...p, mode: 'existing' }))} />
            Existing equipment
          </label>
        </div>
        {v.mode === 'existing' ? (
          <NdtField label="Equipment unit" hint={equipment.isError ? 'Equipment could not be loaded.' : undefined}>
            <select className={NDT_INPUT} value={v.equipment_id} onChange={set('equipment_id')}>
              <option value="">{equipment.isLoading ? 'Loading…' : 'Choose a unit'}</option>
              {(equipment.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {[e.name, e.serialNumber].filter(Boolean).join(' · ') || '—'}
                </option>
              ))}
            </select>
          </NdtField>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <NdtField label="Name">
              <input className={NDT_INPUT} value={v.name} onChange={set('name')} disabled={locked} required />
            </NdtField>
            <NdtField label="Serial number">
              <input className={NDT_INPUT} value={v.serial} onChange={set('serial')} disabled={locked} />
            </NdtField>
            <NdtField label="Manufacturer">
              <input className={NDT_INPUT} value={v.manufacturer} onChange={set('manufacturer')} disabled={locked} />
            </NdtField>
            <NdtField label="Model">
              <input className={NDT_INPUT} value={v.model} onChange={set('model')} disabled={locked} />
            </NdtField>
          </div>
        )}
        <SpecFields kind={v.kind} interval={v.interval} notes={v.notes} onChange={(k, value) => setV((p) => ({ ...p, [k]: value }))} />
      </form>
    </NdtDialog>
  );
}

function SpecFields({
  kind,
  interval,
  notes,
  onChange,
}: {
  kind: string;
  interval: string;
  notes: string;
  onChange: (k: 'kind' | 'interval' | 'notes', value: string) => void;
}) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <NdtField label="Kind">
          <select className={NDT_INPUT} value={kind} onChange={(e) => onChange('kind', e.target.value)}>
            {NDT_INSTRUMENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {NDT_INSTRUMENT_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </NdtField>
        <NdtField label="Calibration interval (days)" hint="Sets the due date when a calibration gives none.">
          <input className={NDT_INPUT} inputMode="numeric" value={interval} onChange={(e) => onChange('interval', e.target.value)} />
        </NdtField>
      </div>
      <NdtField label="Notes">
        <textarea className={`${NDT_INPUT} h-20 py-2`} value={notes} onChange={(e) => onChange('notes', e.target.value)} />
      </NdtField>
    </>
  );
}

export function EditSpecsDialog({ open, onClose, instrument }: { open: boolean; onClose: () => void; instrument: NdtInstrument }) {
  const update = useUpdateNdtSpecs();
  const { v, setV, error, setError } = useForm(open, () => ({
    kind: instrument.specs?.instrumentKind ?? 'other',
    interval: instrument.specs?.calibrationIntervalDays != null ? String(instrument.specs.calibrationIntervalDays) : '',
    notes: instrument.specs?.notes ?? '',
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const interval = intFromInput(v.interval);
    if (Number.isNaN(interval)) return setError('Calibration interval must be a whole number of days.');
    update.mutate(
      { id: instrument.id, body: { instrument_kind: v.kind, calibration_interval_days: interval, notes: textOrNull(v.notes) } },
      {
        onSuccess: () => {
          toast.success('Specs saved');
          onClose();
        },
        onError: (err) => setError(ndtErrorMessage(err, 'The specs could not be saved.')),
      },
    );
  };
  return (
    <NdtDialog open={open} title="Edit specs" onClose={onClose} footer={<Footer formId="ndt-specs" pending={update.isPending} label="Save" onClose={onClose} />}>
      <form id="ndt-specs" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <SpecFields kind={v.kind} interval={v.interval} notes={v.notes} onChange={(k, value) => setV((p) => ({ ...p, [k]: value }))} />
      </form>
    </NdtDialog>
  );
}

export function CalibrationDialog({
  open,
  onClose,
  instrument,
  today,
}: {
  open: boolean;
  onClose: () => void;
  instrument: NdtInstrument;
  today: string | null;
}) {
  const record = useRecordNdtCalibration();
  const interval = instrument.specs?.calibrationIntervalDays ?? null;
  const { v, set, error, setError } = useForm(open, () => ({
    calibrated_on: today ?? '',
    due_on: '',
    certificate_number: '',
    performed_by: '',
    notes: '',
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.calibrated_on) return setError('Calibrated date is required.');
    if (!v.due_on && !interval) return setError('Due date is required: this instrument has no calibration interval.');
    record.mutate(
      {
        id: instrument.id,
        body: {
          calibrated_on: v.calibrated_on,
          due_on: v.due_on || null,
          certificate_number: textOrNull(v.certificate_number),
          performed_by: textOrNull(v.performed_by),
          notes: textOrNull(v.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success('Calibration recorded');
          onClose();
        },
        onError: (err) => setError(ndtErrorMessage(err, 'The calibration could not be recorded.')),
      },
    );
  };
  return (
    <NdtDialog open={open} title="Log calibration" onClose={onClose} footer={<Footer formId="ndt-cal" pending={record.isPending} label="Save" onClose={onClose} />}>
      <form id="ndt-cal" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <NdtField label="Calibrated">
            <input type="date" className={NDT_DATE_INPUT} value={v.calibrated_on} onChange={set('calibrated_on')} />
          </NdtField>
          <NdtField label="Due" hint={interval ? `Blank = calibrated + ${interval} days.` : undefined}>
            <input type="date" className={NDT_DATE_INPUT} value={v.due_on} onChange={set('due_on')} />
          </NdtField>
          <NdtField label="Certificate #">
            <input className={NDT_INPUT} value={v.certificate_number} onChange={set('certificate_number')} />
          </NdtField>
          <NdtField label="Performed by">
            <input className={NDT_INPUT} value={v.performed_by} onChange={set('performed_by')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

// ─── Sources ─────────────────────────────────────────────────────────────────

interface SourceValues {
  isotope: string;
  serial_number: string;
  device_equipment_id: string;
  reference_activity: string;
  reference_on: string;
  licence_number: string;
  status: string;
  leak_test_last_on: string;
  leak_test_due_on: string;
  notes: string;
}

export function SourceDialog({
  open,
  onClose,
  source,
  units,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  source?: NdtSource | null;
  units: NdtUnits | null;
  onCreated?: (id: string) => void;
}) {
  const catalog = useNdtCatalog();
  const equipment = useNdtEquipmentOptions(open);
  const create = useCreateNdtSource();
  const update = useUpdateNdtSource();
  const { v, set, error, setError, start } = useForm<SourceValues>(open, () => ({
    isotope: source?.isotope ?? '',
    serial_number: source?.serialNumber ?? '',
    device_equipment_id: source?.deviceEquipmentId ?? '',
    reference_activity: inputValue(source?.referenceActivity),
    reference_on: source?.referenceOn ?? '',
    licence_number: source?.licenceNumber ?? '',
    status: source?.status ?? 'in_service',
    leak_test_last_on: source?.leakTest.lastOn ?? '',
    leak_test_due_on: source?.leakTest.dueOn ?? '',
    notes: source?.notes ?? '',
  }));
  const activityUnit = units?.activity ?? null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.isotope) return setError('Choose the isotope.');
    if (!v.serial_number.trim()) return setError('Serial number is required.');
    const activity = numberFromInput(v.reference_activity);
    if (activity === null || Number.isNaN(activity)) return setError('Reference activity must be a number.');
    if (!v.reference_on) return setError('Reference date is required.');
    const body: NdtSourceWrite = {
      isotope: v.isotope,
      serial_number: v.serial_number.trim(),
      device_equipment_id: v.device_equipment_id || null,
      reference_activity: activity,
      reference_on: v.reference_on,
      licence_number: textOrNull(v.licence_number),
      status: v.status,
      leak_test_last_on: v.leak_test_last_on || null,
      notes: textOrNull(v.notes),
    };
    // A blank due day is left off, so the API derives it from the last test (+ the tenant leak-test interval).
    if (v.leak_test_due_on) body.leak_test_due_on = v.leak_test_due_on;
    // The API's sentence for every refusal (the leak-test 400s, source_checked_out),
    // a plain one for a status the register holds (status_not_patchable).
    const onError = (err: unknown) => setError(ndtSourceWriteErrorMessage(err, 'The source could not be saved.'));
    if (source) {
      update.mutate({ id: source.id, body: changedOnly(body, v, start) }, { onSuccess: () => { toast.success('Source saved'); onClose(); }, onError });
    } else {
      create.mutate(body, {
        onSuccess: (res) => {
          toast.success('Source added');
          onClose();
          if (res.data?.id) onCreated?.(res.data.id);
        },
        onError,
      });
    }
  };

  return (
    <NdtDialog
      open={open}
      title={source ? 'Edit source' : 'Add source'}
      onClose={onClose}
      wide
      footer={<Footer formId="ndt-source" pending={create.isPending || update.isPending} label="Save" onClose={onClose} />}
    >
      <form id="ndt-source" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <NdtField label="Isotope">
            <select className={NDT_INPUT} value={v.isotope} onChange={set('isotope')}>
              <option value="">Choose an isotope</option>
              {(catalog.data?.data?.isotopes ?? []).map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Serial number">
            <input className={NDT_INPUT} value={v.serial_number} onChange={set('serial_number')} />
          </NdtField>
          <NdtField label={`Reference activity${activityUnit ? ` (${activityUnit})` : ''}`} hint="As on the source certificate.">
            <input className={NDT_INPUT} inputMode="decimal" value={v.reference_activity} onChange={set('reference_activity')} />
          </NdtField>
          <NdtField label="Reference date">
            <input type="date" className={NDT_DATE_INPUT} value={v.reference_on} onChange={set('reference_on')} />
          </NdtField>
          <NdtField label="Exposure device" hint={equipment.isError ? 'Equipment could not be loaded.' : undefined}>
            <select className={NDT_INPUT} value={v.device_equipment_id} onChange={set('device_equipment_id')}>
              <option value="">None</option>
              {(equipment.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {[e.name, e.serialNumber].filter(Boolean).join(' · ') || '—'}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Licence #">
            <input className={NDT_INPUT} value={v.licence_number} onChange={set('licence_number')} />
          </NdtField>
          <NdtField label="Status">
            <select className={NDT_INPUT} value={v.status} onChange={set('status')}>
              {/* A status only the register sets (lost / abandoned in hole) is shown as the current
                  value, so the field never reads as another status; changing it is the API's call. */}
              {!(NDT_SOURCE_SETTABLE_STATUSES as readonly string[]).includes(v.status) && (
                <option value={v.status}>{ndtSourceStatusSkin(v.status).label}</option>
              )}
              {NDT_SOURCE_SETTABLE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {NDT_SOURCE_STATUS_SKIN[s].label}
                </option>
              ))}
            </select>
          </NdtField>
          <div />
          <NdtField label="Last leak test">
            <input type="date" className={NDT_DATE_INPUT} value={v.leak_test_last_on} onChange={set('leak_test_last_on')} />
          </NdtField>
          <NdtField label="Leak test due" hint="Blank = last test + the leak-test interval (6 months unless set).">
            <input type="date" className={NDT_DATE_INPUT} value={v.leak_test_due_on} onChange={set('leak_test_due_on')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

const EVENT_TITLE: Record<NdtSourceEvent, string> = {
  check_out: 'Check out source',
  check_in: 'Check in source',
  transfer: 'Transfer source',
};

/**
 * A custody event. Check-out and transfer name who holds the source after it;
 * a field worker may only name themselves (the API refuses anything else with
 * `not_self`), so the picker is fixed to them. Every refusal code renders as a
 * plain sentence (lib/ndt-inspection.ts NDT_SOURCE_LOG_MESSAGES).
 */
export function SourceLogDialog({
  open,
  onClose,
  source,
  event,
  selfId,
  selfOnly,
}: {
  open: boolean;
  onClose: () => void;
  source: NdtSource;
  event: NdtSourceEvent;
  selfId: string | null;
  selfOnly: boolean;
}) {
  const log = useLogNdtSource();
  const { v, set, error, setError } = useForm(open, () => ({
    user_id: selfOnly ? selfId ?? '' : '',
    location: '',
    job_ref: '',
    survey: '',
    notes: '',
  }));
  const needsHolder = event !== 'check_in';
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (needsHolder && !v.user_id) return setError('Choose who holds the source.');
    const survey = numberFromInput(v.survey);
    if (Number.isNaN(survey)) return setError('Survey reading must be a number.');
    log.mutate(
      {
        id: source.id,
        body: {
          event,
          user_id: needsHolder ? v.user_id : null,
          location: textOrNull(v.location),
          job_ref: textOrNull(v.job_ref),
          survey_reading_usv_h: survey,
          notes: textOrNull(v.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success(EVENT_TITLE[event].replace(' source', '') + ' saved');
          onClose();
        },
        onError: (err) => setError(ndtSourceLogErrorMessage(err)),
      },
    );
  };
  return (
    <NdtDialog open={open} title={EVENT_TITLE[event]} onClose={onClose} footer={<Footer formId="ndt-source-log" pending={log.isPending} label="Save" onClose={onClose} />}>
      <form id="ndt-source-log" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          {needsHolder &&
            (selfOnly ? (
              <NdtField label={event === 'transfer' ? 'New holder' : 'Holder'}>
                <input className={NDT_INPUT} value="You" disabled />
              </NdtField>
            ) : (
              <PeopleSelect label={event === 'transfer' ? 'New holder' : 'Holder'} value={v.user_id} onChange={set('user_id')} required />
            ))}
          <NdtField label="Location">
            <input className={NDT_INPUT} value={v.location} onChange={set('location')} placeholder="Site, LSD or yard" />
          </NdtField>
          <NdtField label="Job ref">
            <input className={NDT_INPUT} value={v.job_ref} onChange={set('job_ref')} />
          </NdtField>
          <NdtField label="Survey reading (µSv/h)">
            <input className={NDT_INPUT} inputMode="decimal" value={v.survey} onChange={set('survey')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

// ─── Dosimetry ───────────────────────────────────────────────────────────────

export function DoseDialog({
  open,
  onClose,
  dose,
  units,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  dose?: NdtDose | null;
  units: NdtUnits | null;
  /** Edit only: offers Delete in the footer; the caller confirms. */
  onDelete?: () => void;
}) {
  const create = useCreateNdtDose();
  const update = useUpdateNdtDose();
  const { v, set, error, setError, start } = useForm(open, () => ({
    user_id: dose?.userId ?? '',
    period_start: dose?.periodStart ?? '',
    period_end: dose?.periodEnd ?? '',
    dosimeter_kind: dose?.dosimeterKind ?? 'badge',
    dosimeter_serial: dose?.dosimeterSerial ?? '',
    deep_dose: inputValue(dose?.deepDose),
    shallow_dose: inputValue(dose?.shallowDose),
    notes: dose?.notes ?? '',
  }));
  const unit = units?.dose ?? null;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.user_id) return setError('Choose the worker.');
    if (!v.period_start || !v.period_end) return setError('Period start and end are required.');
    const deep = numberFromInput(v.deep_dose);
    const shallow = numberFromInput(v.shallow_dose);
    if (deep === null || Number.isNaN(deep)) return setError('Deep dose must be a number.');
    if (Number.isNaN(shallow)) return setError('Shallow dose must be a number.');
    const body: NdtDoseWrite = {
      user_id: v.user_id,
      period_start: v.period_start,
      period_end: v.period_end,
      dosimeter_kind: v.dosimeter_kind,
      dosimeter_serial: textOrNull(v.dosimeter_serial),
      deep_dose: deep,
      shallow_dose: shallow,
      notes: textOrNull(v.notes),
    };
    const done = {
      onSuccess: () => {
        toast.success(dose ? 'Dose record saved' : 'Dose record added');
        onClose();
      },
      onError: (err: unknown) => setError(ndtErrorMessage(err, 'The dose record could not be saved.')),
    };
    if (dose) update.mutate({ id: dose.id, body: changedOnly(body, v, start) }, done);
    else create.mutate(body, done);
  };
  return (
    <NdtDialog
      open={open}
      title={dose ? 'Edit dose record' : 'Add dose record'}
      onClose={onClose}
      footer={
        <>
          {dose && onDelete && (
            <button type="button" className={`${NDT_SECONDARY} mr-auto`} onClick={onDelete}>
              Delete
            </button>
          )}
          <Footer formId="ndt-dose" pending={create.isPending || update.isPending} label="Save" onClose={onClose} />
        </>
      }
    >
      <form id="ndt-dose" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PeopleSelect label="Worker" value={v.user_id} onChange={set('user_id')} required />
          <NdtField label="Dosimeter">
            <select className={NDT_INPUT} value={v.dosimeter_kind} onChange={set('dosimeter_kind')}>
              {NDT_DOSIMETER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {NDT_DOSIMETER_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Period start">
            <input type="date" className={NDT_DATE_INPUT} value={v.period_start} onChange={set('period_start')} />
          </NdtField>
          <NdtField label="Period end">
            <input type="date" className={NDT_DATE_INPUT} value={v.period_end} onChange={set('period_end')} />
          </NdtField>
          <NdtField label={`Deep dose${unit ? ` (${unit})` : ''}`}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.deep_dose} onChange={set('deep_dose')} />
          </NdtField>
          <NdtField label={`Shallow dose${unit ? ` (${unit})` : ''}`}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.shallow_dose} onChange={set('shallow_dose')} />
          </NdtField>
          <NdtField label="Dosimeter serial">
            <input className={NDT_INPUT} value={v.dosimeter_serial} onChange={set('dosimeter_serial')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

// ─── Delete confirm ──────────────────────────────────────────────────────────

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const id = useMemo(() => `ndt-confirm-${title.replace(/\W+/g, '-').toLowerCase()}`, [title]);
  return (
    <NdtDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={NDT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" id={id} className={NDT_PRIMARY} disabled={pending} onClick={onConfirm}>
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <NdtFormError message={error} />
        <p className="text-sm text-fieldiq-text">{body}</p>
      </div>
    </NdtDialog>
  );
}
